
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Clock, BarChart2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

interface TimeEntry {
  id: string;
  duration: number;
  started_at: string;
  ended_at: string;
  type: string;
  tasks: {
    id: string;
    content: string;
    columns: {
      title: string;
      boards: {
        name: string;
      };
    };
  };
}

export default function TimeTracking() {
  const { t } = useTranslation();

  const { data: timeEntries = [], isLoading } = useQuery<TimeEntry[]>({
    queryKey: ['time-entries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('time_entries')
        .select(`
          id,
          duration,
          started_at,
          ended_at,
          type,
          tasks (
            id,
            content,
            columns (
              title,
              boards (
                name
              )
            )
          )
        `)
        .order('started_at', { ascending: false });

      if (error) throw error;
      return data || [];
    }
  });

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Calcular estadísticas
  const totalTime = timeEntries.reduce((acc, entry) => acc + entry.duration, 0);
  const averageSessionTime = timeEntries.length > 0 
    ? Math.round(totalTime / timeEntries.length)
    : 0;

  // Preparar datos para el gráfico
  const chartData = timeEntries.reduce((acc: any[], entry) => {
    const date = format(new Date(entry.started_at), 'MM/dd');
    const existingDate = acc.find(item => item.date === date);
    
    if (existingDate) {
      existingDate.time += entry.duration / 3600; // Convertir a horas
    } else {
      acc.push({
        date,
        time: entry.duration / 3600
      });
    }
    
    return acc;
  }, []);

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-8">
        <Clock className="h-8 w-8" />
        <h1 className="text-2xl font-bold">{t('Time Tracking')}</h1>
      </div>

      {/* Estadísticas generales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              {t('Total Time')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatDuration(totalTime)}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              {t('Sessions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {timeEntries.length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              {t('Average Session')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatDuration(averageSessionTime)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico de tiempo */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center gap-2">
            <BarChart2 className="h-5 w-5" />
            <CardTitle>{t('Time Distribution')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip 
                  formatter={(value: number) => [`${value.toFixed(1)}h`, 'Time']}
                />
                <Bar dataKey="time" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Lista de entradas de tiempo */}
      {isLoading ? (
        <div className="text-center py-8">{t('Loading...')}</div>
      ) : timeEntries.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-center text-muted-foreground">
              {t('No time entries recorded yet')}
            </CardTitle>
          </CardHeader>
        </Card>
      ) : (
        <div className="space-y-4">
          {timeEntries.map((entry) => (
            <Card key={entry.id}>
              <CardHeader>
                <CardTitle className="text-lg">{entry.tasks.content}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-muted-foreground">
                  <p>{t('Board')}: {entry.tasks.columns.boards.name}</p>
                  <p>{t('List')}: {entry.tasks.columns.title}</p>
                  <p>{t('Duration')}: {formatDuration(entry.duration)}</p>
                  <p>{t('Started')}: {format(new Date(entry.started_at), 'PPp')}</p>
                  {entry.ended_at && (
                    <p>{t('Ended')}: {format(new Date(entry.ended_at), 'PPp')}</p>
                  )}
                  <p>{t('Type')}: {entry.type}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
